
import { useState, useEffect } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useToast } from "@/components/ui/use-toast";
import { Search } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

interface LicensePlateInputProps {
  onVehicleDataFetched: (data: any) => void;
}

interface RecentSearch {
  license_plate: string;
  searched_at: string;
}

const KENTEKEN_FORMATS = [
  /^[A-Z]{2}-[A-Z]{2}-\d{2}$/, // XX-XX-99
  /^[A-Z]{2}-\d{2}-[A-Z]{2}$/, // XX-99-XX
  /^\d{2}-[A-Z]{2}-[A-Z]{2}$/, // 99-XX-XX
  /^\d{2}-[A-Z]{3}-\d{1}$/, // 99-XXX-9
  /^\d{1}-[A-Z]{3}-\d{2}$/, // 9-XXX-99
  /^[A-Z]{2}-\d{3}-[A-Z]{1}$/, // XX-999-X
  /^[A-Z]{1}-\d{3}-[A-Z]{2}$/, // X-999-XX
  /^[A-Z]{3}-\d{2}-[A-Z]{1}$/, // XXX-99-X
  /^[A-Z]{1}-\d{2}-[A-Z]{3}$/, // X-99-XXX
  /^\d{1}-[A-Z]{2}-\d{3}$/, // 9-XX-999
  /^[A-Z]{2}-[A-Z]{3}-\d{1}$/, // XX-XXX-9
  /^[B][A-Z]{2}-\d{2}-[A-Z]{1}$/, // BXX-99-X
  /^[B][A-Z]{2}-[A-Z]{2}-\d{1}$/, // BXX-XX-9
  /^[B][A-Z]{2}-[A-Z]{1}-\d{2}$/, // BXX-X-99
];

const LicensePlateInput = ({ onVehicleDataFetched }: LicensePlateInputProps) => {
  const [licensePlate, setLicensePlate] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [recentSearches, setRecentSearches] = useState<RecentSearch[]>([]);
  const { toast } = useToast();

  useEffect(() => {
    fetchRecentSearches();
  }, []);

  const fetchRecentSearches = async () => {
    const { data: userData } = await supabase.auth.getUser();
    if (!userData.user) return;

    const { data } = await supabase
      .from('recent_searches')
      .select('license_plate, searched_at')
      .order('searched_at', { ascending: false })
      .limit(3);

    if (data) {
      setRecentSearches(data);
    }
  };

  const isValidLicensePlate = (plate: string): boolean => {
    return KENTEKEN_FORMATS.some(format => format.test(plate));
  };

  const formatLicensePlate = (value: string) => {
    // Verwijder alle niet-alfanumerieke tekens en converteer naar hoofdletters
    const cleaned = value.replace(/[^A-Z0-9]/g, '').toUpperCase();
    
    // Bij minder dan 2 karakters, return direct
    if (cleaned.length <= 2) {
      return cleaned;
    }
    
    // Voor kentekens met 6 karakters
    if (cleaned.length === 6) {
      // Als het begint met een letter en gevolgd wordt door cijfers (bijv. K520NB)
      if (/^[A-Z]\d{3}[A-Z]{2}$/.test(cleaned)) {
        return `${cleaned.slice(0, 1)}-${cleaned.slice(1, 4)}-${cleaned.slice(4)}`;
      }
      // Standaard formaat XX-XX-99, XX-99-XX, etc.
      return `${cleaned.slice(0, 2)}-${cleaned.slice(2, 4)}-${cleaned.slice(4)}`;
    }
    
    // Voor kentekens met 7 karakters
    if (cleaned.length === 7) {
      // Als het begint met de letter B (bijv. BXX-99-X)
      if (cleaned.startsWith('B')) {
        return `${cleaned.slice(0, 3)}-${cleaned.slice(3, 5)}-${cleaned.slice(5)}`;
      }
      // Als het begint met 2 cijfers (bijv. 99-XXX-9)
      if (/^\d{2}/.test(cleaned)) {
        return `${cleaned.slice(0, 2)}-${cleaned.slice(2, 5)}-${cleaned.slice(5)}`;
      }
      // Als het begint met 1 cijfer (bijv. 9-XXX-99)
      if (/^\d/.test(cleaned)) {
        return `${cleaned.slice(0, 1)}-${cleaned.slice(1, 4)}-${cleaned.slice(4)}`;
      }
      // Als het begint met een letter (bijv. X-999-XX)
      return `${cleaned.slice(0, 1)}-${cleaned.slice(1, 4)}-${cleaned.slice(4)}`;
    }
    
    // Als het kenteken nog niet compleet is, formatteren we het als losse delen
    const parts: string[] = [];
    let remaining = cleaned;
    while (remaining.length > 0) {
      parts.push(remaining.slice(0, Math.min(3, remaining.length)));
      remaining = remaining.slice(Math.min(3, remaining.length));
    }
    return parts.join('-');
  };

  const handleLicensePlateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target.value.toUpperCase();
    const cleaned = input.replace(/[^A-Z0-9]/g, '');
    if (cleaned.length <= 7) {
      setLicensePlate(formatLicensePlate(cleaned));
    }
  };

  const saveRecentSearch = async (plate: string) => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    await supabase
      .from('recent_searches')
      .upsert({
        user_id: user.id,
        license_plate: plate,
        searched_at: new Date().toISOString(),
      }, {
        onConflict: 'user_id,license_plate'
      });

    fetchRecentSearches();
  };

  const fetchVehicleData = async (plateNumber: string = licensePlate) => {
    if (!isValidLicensePlate(plateNumber)) {
      toast({
        variant: "destructive",
        title: "Ongeldig kenteken",
        description: "Voer een geldig Nederlands kenteken in",
      });
      return;
    }

    setIsLoading(true);
    try {
      const cleanLicensePlate = plateNumber.replace(/-/g, '');
      
      const vehicleResponse = await fetch(
        `https://opendata.rdw.nl/resource/m9d7-ebf2.json?kenteken=${cleanLicensePlate}`
      );
      const [vehicleInfo] = await vehicleResponse.json();

      if (!vehicleInfo) {
        throw new Error('Voertuig niet gevonden');
      }

      const fuelResponse = await fetch(
        `https://opendata.rdw.nl/resource/8ys7-d773.json?kenteken=${cleanLicensePlate}`
      );
      const [fuelInfo] = await fuelResponse.json();

      const vehicleData = {
        brand: vehicleInfo?.merk || 'Onbekend',
        model: vehicleInfo?.handelsbenaming || 'Onbekend',
        year: parseInt(vehicleInfo?.datum_eerste_toelating?.substring(0, 4)) || null,
        color: vehicleInfo?.eerste_kleur || 'Onbekend',
        fuel_type: fuelInfo?.brandstof_omschrijving || 'Onbekend',
        license_plate: plateNumber,
        registration_date: formatDate(vehicleInfo?.datum_tenaamstelling) || null,
        transmission_type: vehicleInfo?.typegoedkeuring_transmissie_omschrijving || 'Onbekend',
        apk_date: formatDate(vehicleInfo?.vervaldatum_apk) || null
      };
      
      await saveRecentSearch(plateNumber);
      onVehicleDataFetched(vehicleData);
      
      toast({
        title: "Voertuig gevonden",
        description: "De gegevens zijn opgehaald.",
      });
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Fout bij ophalen gegevens",
        description: "Er is iets misgegaan bij het ophalen van de voertuiggegevens.",
      });
      console.error('Error fetching vehicle data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return null;
    
    const year = dateString.substring(0, 4);
    const month = dateString.substring(4, 6);
    const day = dateString.substring(6, 8);
    return `${day}-${month}-${year}`;
  };

  return (
    <div className="space-y-8 max-w-2xl mx-auto">
      <div className="flex flex-col gap-4">
        <div className="flex w-full">
          <div className="h-[60px] w-[60px] bg-[#1C4B9C] flex items-center justify-center rounded-l-lg">
            <Search className="h-8 w-8 text-white" />
          </div>
          <Input
            type="text"
            placeholder="XX-123-X"
            value={licensePlate}
            onChange={handleLicensePlateChange}
            className="text-center uppercase text-5xl font-medium h-[60px] rounded-l-none rounded-r-lg bg-[#F7C748] text-[#5D4E27] placeholder:text-[#5D4E27]/60 border-0 focus-visible:ring-0 focus-visible:ring-offset-0"
            maxLength={8}
          />
        </div>

        <Button
          onClick={() => fetchVehicleData()}
          disabled={isLoading}
          className="bg-[#1C4B9C] hover:bg-[#153C7D] text-white font-medium px-6 py-2 rounded-lg h-auto text-base"
        >
          {isLoading ? "Laden..." : "Zoek kenteken"}
        </Button>
      </div>

      {recentSearches.length > 0 && (
        <div className="text-center">
          <h3 className="text-xl font-medium text-[#5D4E27] mb-4">
            Recente geschiedenis:
          </h3>
          <div className="inline-flex flex-nowrap gap-2 overflow-x-auto justify-center w-full">
            {recentSearches.map((search) => (
              <div
                key={search.license_plate}
                onClick={() => {
                  setLicensePlate(search.license_plate);
                  fetchVehicleData(search.license_plate);
                }}
                className="flex cursor-pointer shrink-0"
              >
                <div className="h-[40px] w-[40px] bg-[#1C4B9C] flex items-center justify-center rounded-l">
                  <Search className="h-5 w-5 text-white" />
                </div>
                <div className="h-[40px] bg-[#F7C748] text-[#5D4E27] px-4 rounded-r flex items-center justify-center font-medium">
                  {search.license_plate}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default LicensePlateInput;
